import type { InteractionType } from '@atlaskit/react-ufo/common';

import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

const countKeys = [
	'version',
	'eligible',
	'matched',
	'mismatched',
	'missingTitle',
	'failed',
	'layoutMeasured',
	'layoutUnavailable',
	'rowIncreased',
	'rowDecreased',
] as const;

// addMetadata is interaction-local state, not automatically part of UFO's analytics payload.
export function getBylineTitleDiagnosticsPayload(interaction: {
	type: InteractionType;
	ufoName: string;
	metaData?: Record<string, unknown>;
}): { bylineTitleDiagnostics?: Record<string, number> } {
	const counts = interaction.metaData?.bylineTitleDiagnostics;
	if (
		interaction.type !== 'page_load' ||
		!['view_page', 'view-page', 'live-edit'].includes(interaction.ufoName) ||
		!counts ||
		typeof counts !== 'object'
	) {
		return {};
	}

	try {
		const config = JSON.parse(
			StatsigConfigurations.getString('confluence_cls_attribution_config', '{}'),
		);
		const experienceName = interaction.ufoName === 'view_page' ? 'view-page' : interaction.ufoName;
		const included =
			experienceName === 'view-page'
				? (config.includeEntries?.['view-page'] ?? config.includeEntries?.view_page)
				: config.includeEntries?.[experienceName];
		if (
			config.bylineTitleDiagnostics !== true ||
			included !== true ||
			(config.loadType !== undefined && !['initial', 'both'].includes(config.loadType))
		) {
			return {};
		}
		// Emit only the fixed numeric schema, even if unrelated metadata is attached accidentally.
		const values = counts as Record<string, unknown>;
		if (
			values.version !== 1 ||
			!countKeys.every((key) => Number.isSafeInteger(values[key]) && Number(values[key]) >= 0)
		) {
			return {};
		}
		return {
			bylineTitleDiagnostics: Object.fromEntries(
				countKeys.map((key) => [key, Number(values[key])]),
			),
		};
	} catch {
		return {};
	}
}
